import { provide, inject, type InjectionKey } from 'vue'
import type { HistoryListItem } from '@/utils/format-edit/historyList'

export type RirekishoExampleSentence = {
  title: string
  description: string
}

export type RirekishoExampleSentences = {
  license?: RirekishoExampleSentence[]
  reason?: RirekishoExampleSentence[]
  promotion?: RirekishoExampleSentence[]
  hobby?: RirekishoExampleSentence[]
  mypr?: RirekishoExampleSentence[]
  request?: RirekishoExampleSentence[]
}

type DesignRirekishoHobby = {
  id: number | string
  name: string
}

export type DesignRirekishoProject = {
  projectTitle: string
  startYear: number | ''
  startMonth: number | ''
  endYear: number | ''
  endMonth: number | ''
  contentsOfProject: string
  envAndScale: string
  position: string
  uuid: string
}

export type DesignRirekishoJobDetail = {
  companyName: string
  startYear: number | ''
  startMonth: number | ''
  endYear: number | ''
  endMonth: number | ''
  isContinuing: boolean
  capitalStock: string
  numberOfEmployees: string
  businessArea: string
  projects: DesignRirekishoProject[]
  uuid: string
}

export type RirekishoFormatData = {
  createDate: Date | null
  writeDate?: string
  educationalHistories: HistoryListItem[]
  jobHistories: HistoryListItem[]
  licenseHistories: HistoryListItem[]
  lastestEdu: string
  lastestJob: string
  commuteDescription: string
  commuteHours: string
  commuteMinutes: string
  spouseNumber: string
  hasSpouse: boolean | null
  underDutyToSupport: boolean | null
  reason: string
  reasonNew: string
  hobby: string
  request: string
  nowNensyu: string
  kibouNensyu: string
  // キャリコネクション(グローバルウェイ)連携で必須。いずれも3件まで
  hopeWorkAreas: string[]
  hopeTurnTime: string
  hopeIndustryCds: string[]
  hopeJobCategoryCds: string[]
  // 初期値を入れるのは各項目1回だけ。ユーザーが消したあとに埋め戻さないためのフラグ
  hopeAreaAutofilled: boolean
  hopeTurnTimeAutofilled: boolean
  hopeSelectAutofilled: boolean
  requestStatement: string
  shogaiLevel: string
  shogaiKind: string
  handbookKind: string
  hospitalVisit: string
  hospitalVisitDescription: string
  skillDescription: string
  disorderFeature: string
  selfDisorderFeature: string
  considerationDesired: string
  notDisplayIjyo?: boolean
  notDisplayIjyoLicense?: boolean
  gradeYear?: string
  foreignStudent?: boolean
  ai_strength?: string
  ai_company_name?: string
  hopeIndustrycategory?: string
  hopeJobcategory?: string
  introName?: string
  introEmail?: string
  relationship?: string
  shogaiKind_etc?: string
  job_category?: string
  skills?: HistoryListItem[]
  hobbies?: DesignRirekishoHobby[]
  jobDetailHistories?: DesignRirekishoJobDetail[]
  mypr?: string
  mainImageSrc?: string
  ai_mypr_good?: string
  ai_mypr_bad?: string
  ai_mypr_hobby?: string
  ai_mypr_wanted?: string
  ai_mypr_appeal?: string
} & Record<string, unknown>

const RIREKISHO_FORMAT_DATA_KEY: InjectionKey<RirekishoFormatData> = Symbol(
  'rirekishoFormatData'
)

export function createEmptyRirekishoFormatData(): RirekishoFormatData {
  return {
    createDate: null,
    educationalHistories: [],
    jobHistories: [],
    licenseHistories: [],
    lastestEdu: '',
    lastestJob: '',
    commuteDescription: '',
    commuteHours: '',
    commuteMinutes: '',
    spouseNumber: '',
    hasSpouse: null,
    underDutyToSupport: null,
    reason: '',
    reasonNew: '',
    hobby: '',
    request: '',
    nowNensyu: '',
    kibouNensyu: '',
    hopeWorkAreas: [],
    hopeTurnTime: '',
    hopeIndustryCds: [],
    hopeJobCategoryCds: [],
    hopeAreaAutofilled: false,
    hopeTurnTimeAutofilled: false,
    hopeSelectAutofilled: false,
    requestStatement: '',
    shogaiLevel: '',
    shogaiKind: '',
    handbookKind: '',
    hospitalVisit: '',
    hospitalVisitDescription: '',
    skillDescription: '',
    disorderFeature: '',
    selfDisorderFeature: '',
    considerationDesired: '',
    notDisplayIjyo: false,
    notDisplayIjyoLicense: false,
    gradeYear: '',
    foreignStudent: false,
    ai_strength: '',
    ai_company_name: '',
    hopeIndustrycategory: '',
    hopeJobcategory: '',
    introName: '',
    introEmail: '',
    relationship: '',
    shogaiKind_etc: '',
    job_category: '',
    skills: [],
    hobbies: [],
    jobDetailHistories: [],
    mypr: '',
    mainImageSrc: '',
    ai_mypr_good: '',
    ai_mypr_bad: '',
    ai_mypr_hobby: '',
    ai_mypr_wanted: '',
    ai_mypr_appeal: ''
  }
}

export function provideRirekishoFormatData(formatData: RirekishoFormatData) {
  provide(RIREKISHO_FORMAT_DATA_KEY, formatData)
}

export function useRirekishoFormatData(): RirekishoFormatData {
  const formatData = inject(RIREKISHO_FORMAT_DATA_KEY)
  if (!formatData) {
    throw new Error('useRirekishoFormatData() must be used under RirekishoEdit')
  }
  return formatData
}
